<template>
  <div class="keeper-roi-container">
    <h1>🏈 Keeper Return on Investment Analysis - 2025 Season 🏈</h1>

    <div class="summary-stats">
      <h2>Summary Statistics</h2>
      <div class="stat-grid">
        <div class="stat-box">
          <div class="stat-label">Total Keepers</div>
          <div class="stat-value">{{ summaryStats.totalKeepers }}</div>
        </div>
        <div class="stat-box">
          <div class="stat-label">Avg Points/Dollar</div>
          <div class="stat-value">{{ summaryStats.avgPointsPerDollar }}</div>
        </div>
        <div class="stat-box">
          <div class="stat-label">Avg Consistency</div>
          <div class="stat-value">{{ summaryStats.avgConsistency }}%</div>
        </div>
        <div class="stat-box">
          <div class="stat-label">Best Keeper</div>
          <div class="stat-value best-keeper">
            {{ summaryStats.bestKeeper }}
          </div>
        </div>
      </div>
    </div>

    <div class="chart-container">
      <div id="pointsPerDollar"></div>
    </div>

    <div class="chart-container">
      <div id="gradeDistribution"></div>
    </div>

    <div class="chart-container">
      <div id="consistencyChart"></div>
    </div>

    <div class="chart-container">
      <div id="boomBustChart"></div>
    </div>

    <div class="chart-container">
      <div id="costVsPoints"></div>
    </div>

    <h2 class="section-title">Individual Keeper Analysis</h2>

    <div v-for="keeper in keepers" :key="keeper.name" class="keeper-card">
      <div class="keeper-header">
        <div>
          <div class="keeper-name">
            {{ keeper.name }} {{ keeper.active ? "✅" : "" }}
          </div>
          <div class="keeper-subtitle">
            {{ keeper.position }} - {{ keeper.team }} | {{ keeper.owner }} ({{
              keeper.teamName
            }})
          </div>
        </div>
        <div
          class="keeper-grade"
          :style="{ backgroundColor: keeper.gradeColor }"
        >
          {{ keeper.grade }}
        </div>
      </div>

      <div class="keeper-details">
        <div class="detail-section">
          <div class="detail-title">💰 Cost Analysis</div>
          <div class="detail-item">
            <span class="detail-label">Keeper Cost:</span>
            <span class="detail-value">${{ keeper.keeperCost }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">vs Market:</span>
            <span class="detail-value">{{ keeper.vsMarket }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Points/Dollar:</span>
            <span class="detail-value">{{ keeper.pointsPerDollar }}</span>
          </div>
        </div>

        <div class="detail-section">
          <div class="detail-title">📊 Performance</div>
          <div class="detail-item">
            <span class="detail-label">Total Points:</span>
            <span class="detail-value">{{ keeper.totalPoints }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Avg/Game:</span>
            <span class="detail-value">{{ keeper.avgPerGame }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Games Played:</span>
            <span class="detail-value">{{ keeper.gamesPlayed }}</span>
          </div>
        </div>

        <div class="detail-section">
          <div class="detail-title">🏥 Availability</div>
          <div class="detail-item">
            <span class="detail-label">Availability:</span>
            <span
              class="detail-value"
              :style="{ color: keeper.availabilityColor }"
              >{{ keeper.availability }}%</span
            >
          </div>
          <div class="detail-item">
            <span class="detail-label">Games Missed:</span>
            <span class="detail-value">{{ keeper.gamesMissed }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Injury Penalty:</span>
            <span class="detail-value">{{ keeper.injuryPenalty }} pts</span>
          </div>
        </div>

        <div class="detail-section">
          <div class="detail-title">🎯 Consistency</div>
          <div class="detail-item">
            <span class="detail-label">Consistency Score:</span>
            <span class="detail-value">{{ keeper.consistencyScore }}%</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Above Projection:</span>
            <span class="detail-value">{{ keeper.aboveProjection }} weeks</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Below Projection:</span>
            <span class="detail-value">{{ keeper.belowProjection }} weeks</span>
          </div>
        </div>

        <div class="detail-section">
          <div class="detail-title">💥 Boom/Bust</div>
          <div class="detail-item">
            <span class="detail-label">Boom Weeks:</span>
            <span class="detail-value">{{ keeper.boomWeeks }} 🚀</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Bust Weeks:</span>
            <span class="detail-value">{{ keeper.bustWeeks }} 📉</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Boom/Bust Ratio:</span>
            <span class="detail-value">{{ keeper.boomBustRatio }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Plotly from "plotly.js-dist";

export default {
  name: "KeeperROI",
  data() {
    return {
      summaryStats: {
        totalKeepers: 21,
        avgPointsPerDollar: 8.44,
        avgConsistency: 38.2,
        bestKeeper: "Jaxon Smith-Njigba",
      },
      chartData: {
        players: [
          "Jaxon Smith-Njigba",
          "Jonathan Taylor",
          "Chase Brown",
          "Ladd McConkey",
          "Drake London",
          "Kyren Williams",
          "Trey McBride",
          "Jahmyr Gibbs",
          "Jayden Daniels",
          "Bucky Irving",
          "Tee Higgins",
          "Puka Nacua",
          "Brock Bowers",
          "Ja'Marr Chase",
          "Nico Collins",
          "T.J. Hockenson",
          "George Kittle",
          "Brian Thomas Jr.",
          "Saquon Barkley",
          "Malik Nabers",
          "Joe Burrow",
        ],
        pointsPerDollar: [
          21.85, 11.56, 19.35, 16.49, 8.35, 9.21, 9.44, 5.09, 12.76, 11.0, 7.02,
          4.89, 8.15, 3.4, 4.07, 9.99, 4.81, 4.08, 2.69, 1.8, 1.17,
        ],
        grades: [
          "A+",
          "A",
          "B",
          "B-",
          "B-",
          "C+",
          "C",
          "C",
          "C-",
          "C-",
          "C-",
          "C-",
          "D+",
          "D+",
          "D",
          "F",
          "F",
          "F",
          "F",
          "F",
          "F",
        ],
        colors: [
          "#00AA00",
          "#00CC00",
          "#AADD00",
          "#CCCC00",
          "#CCCC00",
          "#DDAA00",
          "#DD8800",
          "#DD8800",
          "#DD6600",
          "#DD6600",
          "#DD6600",
          "#DD6600",
          "#DD4400",
          "#DD4400",
          "#DD2200",
          "#CC0000",
          "#CC0000",
          "#CC0000",
          "#CC0000",
          "#CC0000",
          "#CC0000",
        ],
        consistency: [
          63.6, 63.6, 45.5, 45.5, 55.6, 45.5, 36.4, 54.5, 16.7, 50.0, 36.4,
          50.0, 25.0, 50.0, 30.0, 27.3, 60.0, 12.5, 9.1, 25.0, 0.0,
        ],
        boomWeeks: [
          7, 6, 4, 3, 4, 3, 4, 5, 0, 1, 2, 4, 1, 4, 3, 1, 3, 1, 1, 1, 0,
        ],
        bustWeeks: [
          1, 4, 5, 5, 3, 5, 3, 4, 2, 2, 4, 4, 6, 4, 5, 7, 1, 6, 6, 3, 1,
        ],
        costs: [
          13, 26, 8, 9, 23, 19, 22, 54, 10, 7, 21, 45, 15, 61, 38, 8, 17, 21,
          63, 34, 17,
        ],
        totalPoints: [
          284.00000000000006, 300.5, 154.8, 148.4, 192.0, 175.0, 207.7, 275.0,
          127.56, 77.0, 147.5, 220.00000000000003, 122.20000000000002, 207.7,
          154.5, 79.9, 81.8, 85.6, 169.3, 61.1, 19.86,
        ],
        shortNames: [
          "Jaxon",
          "Jonathan",
          "Chase",
          "Ladd",
          "Drake",
          "Kyren",
          "Trey",
          "Jahmyr",
          "Jayden",
          "Bucky",
          "Tee",
          "Puka",
          "Brock",
          "Ja'Marr",
          "Nico",
          "T.J.",
          "George",
          "Brian",
          "Saquon",
          "Malik",
          "Joe",
        ],
      },
      keepers: [
        {
          name: "Jaxon Smith-Njigba",
          position: "WR",
          team: "SEA",
          owner: "Devon Poeppelman",
          teamName: "Team Poeppelman",
          active: true,
          grade: "A+",
          gradeColor: "#00AA00",
          keeperCost: 13,
          vsMarket: "$+10 (+43.4%)",
          pointsPerDollar: "21.85",
          totalPoints: "284.0",
          avgPerGame: "25.8",
          gamesPlayed: 11,
          availability: "100.0",
          availabilityColor: "#00AA00",
          gamesMissed: 0,
          injuryPenalty: "-0",
          consistencyScore: "63.6",
          aboveProjection: 7,
          belowProjection: 4,
          boomWeeks: 7,
          bustWeeks: 1,
          boomBustRatio: "7.00",
        },
        {
          name: "Jonathan Taylor",
          position: "RB",
          team: "IND",
          owner: "James Borges",
          teamName: "Sweaty Pitts",
          active: true,
          grade: "A",
          gradeColor: "#00CC00",
          keeperCost: 26,
          vsMarket: "$+15 (+36.8%)",
          pointsPerDollar: "11.56",
          totalPoints: "300.5",
          avgPerGame: "27.3",
          gamesPlayed: 11,
          availability: "100.0",
          availabilityColor: "#00AA00",
          gamesMissed: 0,
          injuryPenalty: "-0",
          consistencyScore: "63.6",
          aboveProjection: 7,
          belowProjection: 4,
          boomWeeks: 6,
          bustWeeks: 4,
          boomBustRatio: "1.50",
        },
        {
          name: "Chase Brown",
          position: "RB",
          team: "CIN",
          owner: "Devon Poeppelman",
          teamName: "Team Poeppelman",
          active: true,
          grade: "B",
          gradeColor: "#AADD00",
          keeperCost: 8,
          vsMarket: "$+28 (+77.9%)",
          pointsPerDollar: "19.35",
          totalPoints: "154.8",
          avgPerGame: "14.1",
          gamesPlayed: 11,
          availability: "100.0",
          availabilityColor: "#00AA00",
          gamesMissed: 0,
          injuryPenalty: "-0",
          consistencyScore: "45.5",
          aboveProjection: 5,
          belowProjection: 6,
          boomWeeks: 4,
          bustWeeks: 5,
          boomBustRatio: "0.80",
        },
        {
          name: "Ladd McConkey",
          position: "WR",
          team: "LAC",
          owner: "Troy Kauffman",
          teamName: "2 Kids, 1 Fantasy League",
          active: true,
          grade: "B-",
          gradeColor: "#CCCC00",
          keeperCost: 9,
          vsMarket: "$+21 (+69.9%)",
          pointsPerDollar: "16.49",
          totalPoints: "148.4",
          avgPerGame: "13.5",
          gamesPlayed: 11,
          availability: "100.0",
          availabilityColor: "#00AA00",
          gamesMissed: 0,
          injuryPenalty: "-0",
          consistencyScore: "45.5",
          aboveProjection: 5,
          belowProjection: 6,
          boomWeeks: 3,
          bustWeeks: 5,
          boomBustRatio: "0.60",
        },
        {
          name: "Drake London",
          position: "WR",
          team: "ATL",
          owner: "James Borges",
          teamName: "Sweaty Pitts",
          active: true,
          grade: "B-",
          gradeColor: "#CCCC00",
          keeperCost: 23,
          vsMarket: "$-1 (-5.3%)",
          pointsPerDollar: "8.35",
          totalPoints: "192.0",
          avgPerGame: "17.5",
          gamesPlayed: 11,
          availability: "91.7",
          availabilityColor: "#DDAA00",
          gamesMissed: 1,
          injuryPenalty: "-17.45",
          consistencyScore: "55.6",
          aboveProjection: 5,
          belowProjection: 4,
          boomWeeks: 4,
          bustWeeks: 3,
          boomBustRatio: "1.33",
        },
        {
          name: "Kyren Williams",
          position: "RB",
          team: "LAR",
          owner: "Alan Tebbe",
          teamName: "Rocking Out With My Brock Out",
          active: true,
          grade: "C+",
          gradeColor: "#DDAA00",
          keeperCost: 19,
          vsMarket: "$-4 (-17.1%)",
          pointsPerDollar: "9.21",
          totalPoints: "175.0",
          avgPerGame: "15.9",
          gamesPlayed: 11,
          availability: "100.0",
          availabilityColor: "#00AA00",
          gamesMissed: 0,
          injuryPenalty: "-0",
          consistencyScore: "45.5",
          aboveProjection: 5,
          belowProjection: 6,
          boomWeeks: 3,
          bustWeeks: 5,
          boomBustRatio: "0.60",
        },
        {
          name: "Trey McBride",
          position: "TE",
          team: "ARI",
          owner: "Adam Niemeyer",
          teamName: "Team Niemeyer",
          active: true,
          grade: "C",
          gradeColor: "#DD8800",
          keeperCost: 22,
          vsMarket: "$-5 (-18.1%)",
          pointsPerDollar: "9.44",
          totalPoints: "207.7",
          avgPerGame: "18.9",
          gamesPlayed: 11,
          availability: "100.0",
          availabilityColor: "#00AA00",
          gamesMissed: 0,
          injuryPenalty: "-0",
          consistencyScore: "36.4",
          aboveProjection: 4,
          belowProjection: 7,
          boomWeeks: 4,
          bustWeeks: 3,
          boomBustRatio: "1.33",
        },
        {
          name: "Jahmyr Gibbs",
          position: "RB",
          team: "DET",
          owner: "Jay Eilerman",
          teamName: "Character Issues",
          active: true,
          grade: "C",
          gradeColor: "#DD8800",
          keeperCost: 54,
          vsMarket: "$-2 (-3.8%)",
          pointsPerDollar: "5.09",
          totalPoints: "275.0",
          avgPerGame: "25.0",
          gamesPlayed: 11,
          availability: "100.0",
          availabilityColor: "#00AA00",
          gamesMissed: 0,
          injuryPenalty: "-0",
          consistencyScore: "54.5",
          aboveProjection: 6,
          belowProjection: 5,
          boomWeeks: 5,
          bustWeeks: 4,
          boomBustRatio: "1.25",
        },
        {
          name: "Jayden Daniels",
          position: "QB",
          team: "WAS",
          owner: "Josh Tumbusch",
          teamName: "Just In Time",
          active: true,
          grade: "C-",
          gradeColor: "#DD6600",
          keeperCost: 10,
          vsMarket: "$-22 (-68.7%)",
          pointsPerDollar: "12.76",
          totalPoints: "127.56",
          avgPerGame: "21.3",
          gamesPlayed: 6,
          availability: "50.0",
          availabilityColor: "#DD6600",
          gamesMissed: 6,
          injuryPenalty: "-127.56",
          consistencyScore: "16.7",
          aboveProjection: 1,
          belowProjection: 5,
          boomWeeks: 0,
          bustWeeks: 2,
          boomBustRatio: "0.00",
        },
        {
          name: "Bucky Irving",
          position: "RB",
          team: "TB",
          owner: "Korey Schultz",
          teamName: "Winning Fantasy Is A Fantasy",
          active: true,
          grade: "C-",
          gradeColor: "#DD6600",
          keeperCost: 7,
          vsMarket: "$+3 (+30.0%)",
          pointsPerDollar: "11.0",
          totalPoints: "77.0",
          avgPerGame: "15.4",
          gamesPlayed: 5,
          availability: "41.7",
          availabilityColor: "#DD2200",
          gamesMissed: 7,
          injuryPenalty: "-107.8",
          consistencyScore: "50.0",
          aboveProjection: 2,
          belowProjection: 2,
          boomWeeks: 1,
          bustWeeks: 2,
          boomBustRatio: "0.50",
        },
        {
          name: "Tee Higgins",
          position: "WR",
          team: "CIN",
          owner: "Josh Tumbusch",
          teamName: "Just In Time",
          active: true,
          grade: "C-",
          gradeColor: "#DD6600",
          keeperCost: 21,
          vsMarket: "$-7 (-25.0%)",
          pointsPerDollar: "7.02",
          totalPoints: "147.5",
          avgPerGame: "13.4",
          gamesPlayed: 11,
          availability: "100.0",
          availabilityColor: "#00AA00",
          gamesMissed: 0,
          injuryPenalty: "-0",
          consistencyScore: "36.4",
          aboveProjection: 4,
          belowProjection: 7,
          boomWeeks: 2,
          bustWeeks: 4,
          boomBustRatio: "0.50",
        },
        {
          name: "Puka Nacua",
          position: "WR",
          team: "LAR",
          owner: "Josh Spieles",
          teamName: "Nacua Matata",
          active: true,
          grade: "C-",
          gradeColor: "#DD6600",
          keeperCost: 45,
          vsMarket: "$-10 (-18.1%)",
          pointsPerDollar: "4.89",
          totalPoints: "220.0",
          avgPerGame: "27.5",
          gamesPlayed: 8,
          availability: "66.7",
          availabilityColor: "#DD8800",
          gamesMissed: 4,
          injuryPenalty: "-110.0",
          consistencyScore: "50.0",
          aboveProjection: 4,
          belowProjection: 4,
          boomWeeks: 4,
          bustWeeks: 4,
          boomBustRatio: "1.00",
        },
        {
          name: "Brock Bowers",
          position: "TE",
          team: "LV",
          owner: "Alan Tebbe",
          teamName: "Rocking Out With My Brock Out",
          active: true,
          grade: "D+",
          gradeColor: "#DD4400",
          keeperCost: 15,
          vsMarket: "$-13 (-46.4%)",
          pointsPerDollar: "8.15",
          totalPoints: "122.2",
          avgPerGame: "20.4",
          gamesPlayed: 6,
          availability: "50.0",
          availabilityColor: "#DD6600",
          gamesMissed: 6,
          injuryPenalty: "-122.2",
          consistencyScore: "25.0",
          aboveProjection: 1,
          belowProjection: 3,
          boomWeeks: 1,
          bustWeeks: 6,
          boomBustRatio: "0.17",
        },
        {
          name: "Ja'Marr Chase",
          position: "WR",
          team: "CIN",
          owner: "Clay Brown",
          teamName: "Bijan al Giab",
          active: true,
          grade: "D+",
          gradeColor: "#DD4400",
          keeperCost: 61,
          vsMarket: "$-9 (-12.9%)",
          pointsPerDollar: "3.40",
          totalPoints: "207.7",
          avgPerGame: "18.9",
          gamesPlayed: 11,
          availability: "100.0",
          availabilityColor: "#00AA00",
          gamesMissed: 0,
          injuryPenalty: "-0",
          consistencyScore: "50.0",
          aboveProjection: 5,
          belowProjection: 5,
          boomWeeks: 4,
          bustWeeks: 4,
          boomBustRatio: "1.00",
        },
        {
          name: "Nico Collins",
          position: "WR",
          team: "HOU",
          owner: "Josh Spieles",
          teamName: "Nacua Matata",
          active: true,
          grade: "D",
          gradeColor: "#DD2200",
          keeperCost: 38,
          vsMarket: "$-9 (-19.1%)",
          pointsPerDollar: "4.07",
          totalPoints: "154.5",
          avgPerGame: "30.9",
          gamesPlayed: 5,
          availability: "41.7",
          availabilityColor: "#DD2200",
          gamesMissed: 7,
          injuryPenalty: "-216.3",
          consistencyScore: "30.0",
          aboveProjection: 3,
          belowProjection: 7,
          boomWeeks: 3,
          bustWeeks: 5,
          boomBustRatio: "0.60",
        },
        {
          name: "T.J. Hockenson",
          position: "TE",
          team: "MIN",
          owner: "Troy Kauffman",
          teamName: "2 Kids, 1 Fantasy League",
          active: true,
          grade: "F",
          gradeColor: "#CC0000",
          keeperCost: 8,
          vsMarket: "$-2 (-20.0%)",
          pointsPerDollar: "9.99",
          totalPoints: "79.9",
          avgPerGame: "7.3",
          gamesPlayed: 11,
          availability: "100.0",
          availabilityColor: "#00AA00",
          gamesMissed: 0,
          injuryPenalty: "-0",
          consistencyScore: "27.3",
          aboveProjection: 3,
          belowProjection: 8,
          boomWeeks: 1,
          bustWeeks: 7,
          boomBustRatio: "0.14",
        },
        {
          name: "George Kittle",
          position: "TE",
          team: "SF",
          owner: "Korey Schultz",
          teamName: "Winning Fantasy Is A Fantasy",
          active: true,
          grade: "F",
          gradeColor: "#CC0000",
          keeperCost: 17,
          vsMarket: "$-8 (-32.0%)",
          pointsPerDollar: "4.81",
          totalPoints: "81.8",
          avgPerGame: "16.4",
          gamesPlayed: 5,
          availability: "41.7",
          availabilityColor: "#DD2200",
          gamesMissed: 7,
          injuryPenalty: "-114.8",
          consistencyScore: "60.0",
          aboveProjection: 3,
          belowProjection: 2,
          boomWeeks: 3,
          bustWeeks: 1,
          boomBustRatio: "3.00",
        },
        {
          name: "Brian Thomas Jr.",
          position: "WR",
          team: "JAX",
          owner: "Clay Brown",
          teamName: "Bijan al Giab",
          active: true,
          grade: "F",
          gradeColor: "#CC0000",
          keeperCost: 21,
          vsMarket: "$-15 (-41.7%)",
          pointsPerDollar: "4.08",
          totalPoints: "85.6",
          avgPerGame: "10.7",
          gamesPlayed: 8,
          availability: "66.7",
          availabilityColor: "#DD8800",
          gamesMissed: 4,
          injuryPenalty: "-42.8",
          consistencyScore: "12.5",
          aboveProjection: 1,
          belowProjection: 7,
          boomWeeks: 1,
          bustWeeks: 6,
          boomBustRatio: "0.17",
        },
        {
          name: "Saquon Barkley",
          position: "RB",
          team: "PHI",
          owner: " Jay Eilerman",
          teamName: "Character Issues",
          active: true,
          grade: "F",
          gradeColor: "#CC0000",
          keeperCost: 63,
          vsMarket: "$-8 (-11.3%)",
          pointsPerDollar: "2.69",
          totalPoints: "169.3",
          avgPerGame: "15.4",
          gamesPlayed: 11,
          availability: "100.0",
          availabilityColor: "#00AA00",
          gamesMissed: 0,
          injuryPenalty: "-0",
          consistencyScore: "9.1",
          aboveProjection: 1,
          belowProjection: 10,
          boomWeeks: 1,
          bustWeeks: 6,
          boomBustRatio: "0.17",
        },
        {
          name: "Malik Nabers",
          position: "WR",
          team: "NYG",
          owner: "Adam Niemeyer",
          teamName: "Team Niemeyer",
          active: true,
          grade: "F",
          gradeColor: "#CC0000",
          keeperCost: 34,
          vsMarket: "$-23 (-40.4%)",
          pointsPerDollar: "1.80",
          totalPoints: "61.1",
          avgPerGame: "15.3",
          gamesPlayed: 4,
          availability: "33.3",
          availabilityColor: "#CC0000",
          gamesMissed: 8,
          injuryPenalty: "-122.4",
          consistencyScore: "25.0",
          aboveProjection: 1,
          belowProjection: 3,
          boomWeeks: 1,
          bustWeeks: 3,
          boomBustRatio: "0.33",
        },
        {
          name: "Joe Burrow",
          position: "QB",
          team: "CIN",
          owner: "Brandon Hoying",
          teamName: "Team Hoying",
          active: true,
          grade: "F",
          gradeColor: "#CC0000",
          keeperCost: 17,
          vsMarket: "$-16 (-48.5%)",
          pointsPerDollar: "1.17",
          totalPoints: "19.86",
          avgPerGame: "19.9",
          gamesPlayed: 1,
          availability: "8.3",
          availabilityColor: "#CC0000",
          gamesMissed: 11,
          injuryPenalty: "-218.9",
          consistencyScore: "0.0",
          aboveProjection: 0,
          belowProjection: 1,
          boomWeeks: 0,
          bustWeeks: 1,
          boomBustRatio: "0.00",
        },
      ],
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.renderCharts();
    });
  },
  methods: {
    renderCharts() {
      // Points per Dollar Chart
      const ppd_data = [
        {
          x: this.chartData.players,
          y: this.chartData.pointsPerDollar,
          type: "bar",
          marker: {
            color: this.chartData.colors,
          },
          text: this.chartData.grades,
          textposition: "outside",
          hovertemplate:
            "<b>%{x}</b><br>Points/Dollar: %{y:.2f}<br>Grade: %{text}<extra></extra>",
        },
      ];

      const ppd_layout = {
        title: "Points Per Dollar by Player",
        xaxis: { title: "Player", tickangle: -45 },
        yaxis: { title: "Points per Dollar" },
        plot_bgcolor: "#1e1e1e",
        paper_bgcolor: "#1e1e1e",
        font: { color: "#d4d4d4" },
        showlegend: false,
        height: 500,
      };

      Plotly.newPlot("pointsPerDollar", ppd_data, ppd_layout);

      // Grade Distribution
      const grade_data = [
        {
          labels: ["A+", "A", "B", "B-", "C+", "C", "C-", "D+", "D", "F"],
          values: [1, 1, 1, 2, 1, 2, 4, 2, 1, 6],
          type: "pie",
          marker: {
            colors: [
              "#00AA00",
              "#00CC00",
              "#AADD00",
              "#CCCC00",
              "#DDAA00",
              "#DD8800",
              "#DD6600",
              "#DD4400",
              "#DD2200",
              "#CC0000",
            ],
          },
          textinfo: "label+value",
          hovertemplate:
            "<b>Grade %{label}</b><br>Count: %{value}<extra></extra>",
        },
      ];

      const grade_layout = {
        title: "Grade Distribution",
        plot_bgcolor: "#1e1e1e",
        paper_bgcolor: "#1e1e1e",
        font: { color: "#d4d4d4" },
        height: 400,
      };

      Plotly.newPlot("gradeDistribution", grade_data, grade_layout);

      // Consistency Chart
      const consistency_data = [
        {
          x: this.chartData.players,
          y: this.chartData.consistency,
          type: "bar",
          marker: {
            color: this.chartData.colors,
          },
          hovertemplate: "<b>%{x}</b><br>Consistency: %{y:.1f}%<extra></extra>",
        },
      ];

      const consistency_layout = {
        title: "Consistency Score (% Weeks Above Projection)",
        xaxis: { title: "Player", tickangle: -45 },
        yaxis: { title: "Consistency Score (%)", range: [0, 100] },
        plot_bgcolor: "#1e1e1e",
        paper_bgcolor: "#1e1e1e",
        font: { color: "#d4d4d4" },
        showlegend: false,
        height: 500,
      };

      Plotly.newPlot("consistencyChart", consistency_data, consistency_layout);

      // Boom/Bust Chart
      const boom_data = {
        x: this.chartData.players,
        y: this.chartData.boomWeeks,
        name: "Boom Weeks",
        type: "bar",
        marker: { color: "#00AA00" },
        hovertemplate: "<b>%{x}</b><br>Boom Weeks: %{y}<extra></extra>",
      };

      const bust_data = {
        x: this.chartData.players,
        y: this.chartData.bustWeeks,
        name: "Bust Weeks",
        type: "bar",
        marker: { color: "#CC0000" },
        hovertemplate: "<b>%{x}</b><br>Bust Weeks: %{y}<extra></extra>",
      };

      const boom_bust_layout = {
        title: "Boom vs Bust Weeks",
        xaxis: { title: "Player", tickangle: -45 },
        yaxis: { title: "Number of Weeks" },
        plot_bgcolor: "#1e1e1e",
        paper_bgcolor: "#1e1e1e",
        font: { color: "#d4d4d4" },
        barmode: "group",
        height: 500,
      };

      Plotly.newPlot("boomBustChart", [boom_data, bust_data], boom_bust_layout);

      // Cost vs Points Scatter
      const scatter_data = [
        {
          x: this.chartData.costs,
          y: this.chartData.totalPoints,
          mode: "markers+text",
          type: "scatter",
          marker: {
            size: 15,
            color: this.chartData.colors,
          },
          text: this.chartData.shortNames,
          textposition: "top center",
          hovertemplate:
            "<b>%{text}</b><br>Cost: $%{x}<br>Points: %{y:.1f}<extra></extra>",
        },
      ];

      const scatter_layout = {
        title: "Keeper Cost vs Total Points",
        xaxis: { title: "Keeper Cost ($)" },
        yaxis: { title: "Total Points" },
        plot_bgcolor: "#1e1e1e",
        paper_bgcolor: "#1e1e1e",
        font: { color: "#d4d4d4" },
        showlegend: false,
        height: 500,
      };

      Plotly.newPlot("costVsPoints", scatter_data, scatter_layout);
    },
  },
};
</script>

<style scoped>
.keeper-roi-container {
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  margin: 20px;
  background-color: #1e1e1e;
  color: #d4d4d4;
}

h1 {
  text-align: center;
  color: #4ec9b0;
}

.chart-container {
  margin: 30px auto;
  max-width: 1400px;
}

.summary-stats {
  background-color: #252526;
  padding: 20px;
  border-radius: 8px;
  margin: 20px auto;
  max-width: 1400px;
  border: 1px solid #3e3e42;
}

.summary-stats h2 {
  color: #4ec9b0;
  margin-top: 0;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 15px;
}

.stat-box {
  background-color: #2d2d30;
  padding: 15px;
  border-radius: 5px;
  border-left: 4px solid #4ec9b0;
}

.stat-label {
  font-size: 0.9em;
  color: #858585;
}

.stat-value {
  font-size: 1.5em;
  font-weight: bold;
  color: #4ec9b0;
  margin-top: 5px;
}

.stat-value.best-keeper {
  font-size: 1.1em;
}

.section-title {
  text-align: center;
  color: #4ec9b0;
  margin-top: 50px;
}

.keeper-card {
  background-color: #252526;
  padding: 20px;
  margin: 15px auto;
  border-radius: 8px;
  max-width: 1400px;
  border: 1px solid #3e3e42;
}

.keeper-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 2px solid #3e3e42;
}

.keeper-name {
  font-size: 1.3em;
  font-weight: bold;
  color: #4ec9b0;
}

.keeper-subtitle {
  color: #858585;
  margin-top: 5px;
}

.keeper-grade {
  font-size: 2em;
  font-weight: bold;
  padding: 10px 20px;
  border-radius: 5px;
  color: white;
}

.keeper-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
}

.detail-section {
  background-color: #2d2d30;
  padding: 15px;
  border-radius: 5px;
}

.detail-title {
  font-weight: bold;
  color: #4ec9b0;
  margin-bottom: 10px;
  font-size: 1.1em;
}

.detail-item {
  margin: 5px 0;
  display: flex;
  justify-content: space-between;
}

.detail-label {
  color: #858585;
}

.detail-value {
  color: #d4d4d4;
  font-weight: 500;
}
</style>
